// admin-panel/src/pages/Categories.jsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoriesApi } from '../lib/api';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Categories() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name:'', slug:'', description:'', sort_order:0, is_active:true });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const { data:cats=[], isLoading } = useQuery({ queryKey:['admin-categories'], queryFn: categoriesApi.list });

  const saveMut = useMutation({
    mutationFn: (d) => modal?.id ? categoriesApi.update(modal.id,d) : categoriesApi.create(d),
    onSuccess: () => { qc.invalidateQueries(['admin-categories']); qc.invalidateQueries(['categories']); setModal(null); toast.success('Saved'); },
    onError: (e) => toast.error(e.message||'Failed'),
  });
  const delMut = useMutation({
    mutationFn: categoriesApi.delete,
    onSuccess: () => { qc.invalidateQueries(['admin-categories']); toast.success('Deleted'); },
  });

  function openEdit(c) {
    setForm({ name:c.name||'', slug:c.slug||'', description:c.description||'', sort_order:c.sort_order||0, is_active:c.is_active??true });
    setModal(c);
  }

  const list = Array.isArray(cats) ? cats : (cats?.data||[]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Categories</h1>
        <button onClick={()=>{setForm({name:'',slug:'',description:'',sort_order:0,is_active:true});setModal('new');}} className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90">
          <Plus size={16}/> Add Category
        </button>
      </div>
      <div className="bg-[#151515] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/5">{['Name','Slug','Projects','Status','Actions'].map(h=><th key={h} className="text-left text-xs text-[#555] font-medium px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {isLoading ? Array(5).fill(0).map((_,i)=><tr key={i} className="border-b border-white/5">{Array(5).fill(0).map((_,j)=><td key={j} className="px-4 py-3"><div className="h-4 bg-white/5 rounded animate-pulse w-24"/></td>)}</tr>) :
            list.map(c => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                <td className="px-4 py-3 text-white font-medium">{c.name}</td>
                <td className="px-4 py-3 text-[#555] font-mono text-xs">{c.slug}</td>
                <td className="px-4 py-3 text-[#888]">{c.projects_count??'—'}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${c.is_active?'bg-green-500/10 text-green-400':'bg-gray-500/10 text-gray-400'}`}>{c.is_active?'Active':'Inactive'}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={()=>openEdit(c)} className="p-1.5 rounded-lg hover:bg-white/10 text-[#888] hover:text-white transition-all"><Edit2 size={13}/></button>
<button onClick={()=>{if(window.confirm('Delete category?'))delMut.mutate(c.id)}} className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#888] hover:text-red-400 transition-all"><Trash2 size={13}/></button>
 </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold">{modal?.id?'Edit':'New'} Category</h3>
              <button onClick={()=>setModal(null)}><X size={18} className="text-[#888] hover:text-white"/></button>
            </div>
            <div className="space-y-3">
              {[{k:'name',l:'Name *',p:'e.g. Living Room'},{k:'slug',l:'Slug',p:'living-room'},{k:'description',l:'Description',p:'Brief description'}].map(({k,l,p})=>(
                <div key={k}>
                  <label className="block text-xs text-[#555] mb-1.5">{l}</label>
                  <input value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={p} className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"/>
                </div>
              ))}
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e=>set('sort_order',e.target.value)} className="w-24 bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30"/>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={()=>setModal(null)} className="flex-1 py-2.5 rounded-xl text-sm text-[#888] bg-white/5 hover:bg-white/10">Cancel</button>
              <button onClick={()=>saveMut.mutate(form)} disabled={saveMut.isPending||!form.name} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-50">
                <Save size={14}/>{saveMut.isPending?'Saving…':'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
